/**
 * Relatietypen: frontend-vocabulaire string-keys (bewust geen enum, B3) — nieuw
 * type = key + i18n-label + icoon hier toevoegen, zonder backend-wijziging.
 * `compositeSource` is gereserveerd voor de latere composite-charts en hoort
 * niet in de kiesbare lijst.
 */
export const HD_RELATION_TYPE_KEYS = [
  'partner',
  'family',
  // Richtingspaar (zoals teacher/student): je kiest de rol van de ándere chart;
  // de andere kant ziet automatisch het inverse (backend ChartRelationTypeHelpers).
  'parent',
  'child',
  'friend',
  'acquaintance',
  'businessPartner',
  'colleague',
  'teacher',
  'student',
  'client',
  'serviceProvider',
  'other',
] as const;

export type HdRelationTypeKey = (typeof HD_RELATION_TYPE_KEYS)[number];

/**
 * Positie van een relatietype in de kiesbare-typen-volgorde (de dropdown in de
 * relatie-dialog); onbekende/systeem-typen sorteren achteraan. De Relaties-lijst
 * volgt dezelfde volgorde.
 */
export function hdRelationTypeOrder(relationType: string | null | undefined): number {
  const index = (HD_RELATION_TYPE_KEYS as readonly string[]).indexOf(relationType ?? '');
  return index === -1 ? HD_RELATION_TYPE_KEYS.length : index;
}

/** FA7-iconen per relatietype (B11 in CHART_MANAGEMENT_PLAN.md). */
export const HD_RELATION_TYPE_ICONS: Readonly<Record<string, string>> = {
  family: 'people-roof',
  parent: 'hands-holding-child',
  child: 'child',
  friend: 'handshake',
  acquaintance: 'hand-wave',
  partner: 'heart',
  businessPartner: 'briefcase',
  colleague: 'id-badge',
  teacher: 'chalkboard-user',
  student: 'graduation-cap',
  client: 'clipboard-user',
  serviceProvider: 'user-tie',
  other: 'link',
  compositeSource: 'user-group',
  hasComposite: 'user-group',
  cycleOf: 'arrows-rotate',
  hasCycle: 'arrows-rotate',
  momentOf: 'calendar-star',
  hasMoment: 'calendar-star',
};

/**
 * Systeem-relatietypen van de cycle-koppeling (spec cycle charts §8):
 * from = cycle chart → to = natal ("cycleOf"), inverse "hasCycle". Nooit in de
 * picker en niet onder "Relaties" getoond — de Cycles-sectie gebruikt ze.
 */
export function isHdCycleRelationType(relationType: string | null | undefined): boolean {
  return relationType === 'cycleOf' || relationType === 'hasCycle';
}

/**
 * Systeem-relatietypen van de composite-koppeling: from = composite chart →
 * to = bronchart ("compositeSource"), inverse "hasComposite". Nooit in de
 * picker en niet onder "Relaties" getoond — de Berekenen/Openen-detectie op de
 * relatie-rijen gebruikt ze.
 */
export function isHdCompositeRelationType(relationType: string | null | undefined): boolean {
  return relationType === 'compositeSource' || relationType === 'hasComposite';
}

/**
 * Systeem-relatietypen van de momenten-koppeling (spec notable moments §3.2):
 * from = moment(-chart) → to = de chart waar het bij hoort ("momentOf"),
 * inverse "hasMoment". Nooit in de picker; de Momenten-sectie gebruikt ze.
 */
export function isHdMomentRelationType(relationType: string | null | undefined): boolean {
  return relationType === 'momentOf' || relationType === 'hasMoment';
}

/**
 * Alle systeem-relatietypen samen — voor het filteren van de menselijke
 * relatie-UI's (relaties-dialog en Relaties-sectie).
 */
export function isHdSystemRelationType(relationType: string | null | undefined): boolean {
  return (
    isHdCycleRelationType(relationType)
    || isHdCompositeRelationType(relationType)
    || isHdMomentRelationType(relationType)
  );
}

export function hdRelationTypeIcon(relationType: string | null | undefined): string {
  return (relationType && HD_RELATION_TYPE_ICONS[relationType]) || HD_RELATION_TYPE_ICONS['other'];
}

/**
 * i18n-label van een relatietype; onbekende keys (bv. door een oudere/nieuwere
 * client geschreven) vallen terug op de rauwe string i.p.v. een i18n-padtekst.
 */
export function hdRelationTypeLabel(
  translate: (key: string) => string,
  relationType: string | null | undefined,
): string {
  if (!relationType) return '—';
  const key = `humanDesign.chartManager.relations.types.${relationType}`;
  const label = translate(key);
  return label === key ? relationType : label;
}
